import { Box, Container, Grid, Typography } from "@mui/material";
import {
    ApartmentOutlined, PaymentsOutlined, EngineeringOutlined,
    PeopleOutline, TrendingUpOutlined, ShieldOutlined
} from "@mui/icons-material";
import { FC } from "react";
import { useInView } from "../../hooks/useInView";

const PRIMARY = '#8c7c0c';

const FEATURES = [
    {
        icon: <ApartmentOutlined sx={{ fontSize: 28, color: PRIMARY }} />,
        title: 'Property Management',
        desc: 'List, manage, and monitor all your properties from one clean dashboard. Track occupancy, vacancies, and performance in real time.',
    },
    {
        icon: <PaymentsOutlined sx={{ fontSize: 28, color: PRIMARY }} />,
        title: 'Rent Collection',
        desc: 'Automated rent reminders, M-Pesa integration, and instant payment records. Never chase a tenant again.',
    },
    {
        icon: <EngineeringOutlined sx={{ fontSize: 28, color: PRIMARY }} />,
        title: 'Maintenance Tracking',
        desc: 'Tenants submit maintenance requests in-app. You assign, track, and close them — all with a full audit trail.',
    },
    {
        icon: <PeopleOutline sx={{ fontSize: 28, color: PRIMARY }} />,
        title: 'Tenant Screening',
        desc: 'Verified tenant profiles, payment history, and ratings help you find reliable tenants fast.',
    },
    {
        icon: <TrendingUpOutlined sx={{ fontSize: 28, color: PRIMARY }} />,
        title: 'Investment Analytics',
        desc: 'Track rental yield, occupancy rates, and market trends. Make data-driven investment decisions.',
    },
    {
        icon: <ShieldOutlined sx={{ fontSize: 28, color: PRIMARY }} />,
        title: 'Deposit Protection',
        desc: 'Sheltergram acts as custodian for tenant deposits on Silver and Gold plans, protecting both landlords and tenants.',
    },
];

const Features: FC = () => {
    const { ref, visible } = useInView();

    return (
        <Box
            component="section"
            id="features"
            ref={ref}
            sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}
        >
            <Container maxWidth="lg">
                {/* Heading */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }} className={`sg-reveal ${visible ? 'sg-visible' : ''}`}>
                    <Typography
                        sx={{ display: 'inline-block', bgcolor: `${PRIMARY}12`, color: PRIMARY, px: 2, py: 0.5, borderRadius: 20, fontSize: 13, fontWeight: 600, mb: 2 }}
                    >
                        Everything You Need
                    </Typography>
                    <Typography component="h2" sx={{ fontSize: { xs: 30, md: 42 }, fontWeight: 800, color: '#111827', mb: 2, lineHeight: 1.2 }}>
                        Built for every role in real estate
                    </Typography>
                    <Typography sx={{ fontSize: 17, color: '#6b7280', maxWidth: 520, mx: 'auto', lineHeight: 1.7 }}>
                        Whether you own one unit or a hundred, Sheltergram has the tools to run your portfolio smoothly.
                    </Typography>
                </Box>

                {/* Feature grid */}
                <Grid container spacing={3}>
                    {FEATURES.map((f, i) => (
                        <Grid item xs={12} sm={6} md={4} key={f.title}>
                            <Box
                                className={`sg-reveal sg-card-lift ${visible ? 'sg-visible' : ''}`}
                                style={{ transitionDelay: `${i * 0.08}s` }}
                                sx={{
                                    p: 3.5,
                                    borderRadius: 3,
                                    border: '1px solid #e5e7eb',
                                    bgcolor: '#fff',
                                    height: '100%',
                                    boxShadow: '0 2px 8px rgba(0,0,0,.04)',
                                }}
                            >
                                <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: 2, bgcolor: `${PRIMARY}10`, mb: 2.5 }}>
                                    {f.icon}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#111827', mb: 1 }}>
                                    {f.title}
                                </Typography>
                                <Typography sx={{ fontSize: 14.5, color: '#6b7280', lineHeight: 1.65 }}>
                                    {f.desc}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default Features;
